import { useAdminAccess } from "src/hooks/useAdminAccess";
import React, { useState, useEffect } from "react";
import { Container } from "./styles";
import { Link, useParams, useHistory } from "react-router-dom";
import Utils, { sendAppEvent } from "../../Utils";
import InlineSVG from "react-inlinesvg";
import InstallButton from "../InstallButton";
import PageNotFound from "../../PageNotFound";
import usePlugin from "../Plugins/hooks/usePlugin";
import dateFormat from "dateformat";
import { availableEngines } from "../../../../src/shared/schemas/PluginSchema"; // Client version of availableEngines
import { pluckDeep } from "../../Utils";
import { useAppStore } from "../../stores/appStore";
import Subscription from "../../models/Subscription";
import IconButton from "../IconButton";
import TextButton from "../TextButton";
import { useToast } from "../ToastWrapper";
import usePublicMode from "../../hooks/usePublicMode";

export default function Plugin({ openLogin }) {
  const { can } = useAdminAccess();
  const params = useParams();
  const [creditUsage, setCreditUsage] = useState();
  const [subscription, setSubscription] = useState(null);
  const { setToast } = useToast();
  const { urlPrefixPlugins, urlPrefixPlugin } = usePublicMode();

  const { id, slug } = params;

  const { plugin, error } = usePlugin(id);

  const history = useHistory();

  const user = useAppStore((state) => state.user);

  const promptVisibilities = {
    VISIBLE: "VISIBLE",
    HIDDEN: "HIDDEN",
  };

  // Analytics
  useEffect(() => {
    sendAppEvent("Plugins - Show Page View", { pluginId: id });
  }, []);

  // Application schema (SEO)
  useEffect(() => {
    if (plugin && !plugin.deletedAt) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "BrowserApplication",
        name: plugin.name,
        description: plugin.description,
        url: window.location.href,
        applicationCategory: "BrowserApplication",
        operatingSystem: "Web",
        author: plugin.userDisplayName,
        dateCreated: plugin.createdAt,
        dateModified: plugin.updatedAt,
      };

      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [plugin]);

  useEffect(() => {
    (async () => {
      if (user) {
        const currentUserSubscription = await Subscription.fetchFromUserId(user.uid);
        setSubscription(currentUserSubscription);
      }
    })();
  }, [user]);

  useEffect(() => {
    console.log("check plugin url");
    if (plugin) {
      let needsChange = false;

      let targetSlug = slug;
      if (plugin.slug !== slug && plugin.slug !== null) {
        targetSlug = plugin.slug;
        needsChange = true;
      }

      let targetId = id;
      if (plugin.shortId !== id && plugin.shortId !== null) {
        targetId = plugin.shortId;
        needsChange = true;
      }

      if (needsChange) {
        history.push(`${urlPrefixPlugin}/${targetId}/${targetSlug}`);
      }
    }
  }, [slug, id, plugin]);

  useEffect(() => {
    const engines = pluckDeep(plugin, "engine");
    if (engines.length > 0) {
      const averageCreditUsageLevel =
        engines.reduce((sum, engine) => {
          return sum + (availableEngines?.[engine]?.creditUsageLevel || 0);
        }, 0) / engines.length;
      const roundedAverage = Math.ceil(averageCreditUsageLevel);
      const clampedAverage = Math.max(1, Math.min(roundedAverage, 4));
      setCreditUsage(clampedAverage);
    }
  }, [plugin?.blocks?.length, availableEngines]);

  if (error) {
    return <PageNotFound />;
  }

  if (!plugin) {
    return (
      <Container>
        <Link to={urlPrefixPlugins}>{`<-`} Back to Plugins</Link>

        <div className="plugin-card">
          <div className="loading">Loading...</div>
        </div>
      </Container>
    );
  }

  const inferenceBlocks = plugin.blocks.filter((b) => b.blockType === "inference");

  const enableEditButton = plugin?.userId === user?.uid || can("admin.tools");
  const enableReportButton = plugin?.userId !== user?.uid || can("admin.tools");

  return (
    <Container className="plugin-container">
      <Link
        className="back-link"
        to={urlPrefixPlugins}
      >
        {`<-`} Back to Plugins
      </Link>

      <div className="plugin-card">
        <div className="header">
          <h1>{plugin.name}</h1>
          <div className="buttons">
            {enableReportButton && (
              <IconButton
                src="/assets/flag.svg"
                className="report"
                onClick={() => (window.location.href = `https://airtable.com/appgau2UiFT3lDHN1/pagE1UNsSwkNQmZWv/form?prefill_Plugin+URL=${encodeURIComponent(window.location)}&prefill_Reporter+Email=${user.email}`)}
                ariaLabel="Report"
              />
            )}

            <IconButton
              src="/assets/chain-linked.svg"
              className="copy-link"
              onClick={(e) => {
                navigator.clipboard.writeText(window.location.href);
                setToast("Link copied to clipboard");
                e.currentTarget.blur();
              }}
              ariaLabel="Copy link"
            />

            <div className="main-buttons">
              {enableEditButton && (
                <TextButton
                  className="edit"
                  onClick={() => history.push(`/app/my-plugin/edit/${id}`)}
                  src="/assets/pencil.svg"
                >
                  Edit
                </TextButton>
              )}

              <InstallButton
                titleCase={true}
                showInstall={true}
                plugin={plugin}

                openLogin={openLogin}
              />
            </div>
          </div>
        </div>

        <div className="plugin-info">
          <div className="plugin-info-box">
            <div className="subhead">Created by</div>
            <div className="main-info author">{plugin.userDisplayName}</div>
          </div>
          <div className="plugin-info-box">
            <div className="subhead">Users</div>
            <div className="main-info user-count">{plugin.installationCount}</div>
          </div>
          {creditUsage && (
            <div className="plugin-info-box">
              <div className="subhead">Credits use</div>
              <div className="main-info credits">
                {Array(4)
                  .fill()
                  .map((_, i) => {
                    return (
                      <InlineSVG
                        src={i < creditUsage ? "/assets/pricing/credits-filled.svg" : "/assets/pricing/credits.svg"}
                        className={`credit ${i >= creditUsage ? "empty" : ""}`}
                      ></InlineSVG>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        <div className="plugin">
          <p className="description">{Utils.nl2br(plugin.description)}</p>

          {plugin.promptVisibility === promptVisibilities.VISIBLE && (
            <>
              {inferenceBlocks.length > 0 && <div className="prompts">Instructions</div>}
              {inferenceBlocks.map((block, index) => {
                return (
                  <div
                    className="prompt-block"
                    key={`prompt-${block.id}`}
                  >
                    <h3>Stage {index + 1}</h3>
                    <p>
                      {block.options.prompt.messages.map((message) => {
                        if (!message.template || !message.role) {
                          return "";
                        }
                        return (
                          <p>
                            <strong>{message.role}</strong>: {Utils.nl2br(message.template)}
                          </p>
                        );
                      })}
                    </p>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <footer>
          <p className="last-updated">Last Updated: {dateFormat(new Date(plugin.updatedAt), "mmmm dS, yyyy")}</p>
        </footer>
      </div>
    </Container>
  );
}
